import { ApiClient } from '@/shared/api-client';
import { IFileBucketFile, IHttpError, IHttpValidationError } from '@/shared/api-client/models';
import { ArgumentValidator, ConsoleFacade } from '@/shared/helpers';
import { IFileFormatConfigSpecification } from '@/shared/models/file-format-config-specification';
import _ from 'lodash';
import Resumable from 'resumablejs';
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';

interface IViewModel {

    loading: boolean;
    errors: FileUploadErrors[];
}

type FileUploadErrors = 'Undefined' | 'InvalidFileType' | 'FileToLarge' | 'FileToSmall';

@Component
export default class FileUploadComponent extends Vue {

    public viewModel: IViewModel = {

        loading: false,
        errors: []
    };

    @Prop() public translations!: { [key: string]: string; };
    @Prop({ default: () => [] }) public restrictedToContentTypes!: IFileFormatConfigSpecification[];
    @Prop({ default: 'btn btn-secondary' }) public browseBtnCssClass!: string;
    @Prop() public maxFileSizeInBytes?: number;

    private fileName: string = '';

    private apiClient: ApiClient = new ApiClient();

    @Emit('fileUploaded')
    public emitFileUploaded(fileBucketFile: IFileBucketFile, file: File): { fileBucketFile: IFileBucketFile, file: File } {

        ArgumentValidator.notNullOrEmpty(file, 'file');

        return { fileBucketFile, file };
    }

    @Emit('httpError')
    public emitHttpError(error: IHttpError): IHttpError {

        ArgumentValidator.notNullOrEmpty(error, 'error');

        return error;
    }

    public hasError(error: FileUploadErrors): boolean {

        return this.viewModel.errors.some(x => x === error) || false;
    }

    public async created(): Promise<void> {

        ConsoleFacade.log('FileImportComponent created.');
    }

    public async mounted(): Promise<void> {

        ConsoleFacade.log('FileImportComponent mounted.');

        await this.createFileUpload();
    }

    private async createFileUpload(): Promise<void> {

        this.viewModel.errors = [];

        let uploadedFile: File;

        const resumable = new Resumable({
            target: '/easyvote-api/fileBucket/chunk',
            chunkNumberParameterName: 'chunkNumber',
            identifierParameterName: 'identifier',
            totalChunksParameterName: 'totalChunks',
            chunkSizeParameterName: 'chunkSize',
            totalSizeParameterName: 'totalSize',
            fileNameParameterName: 'fileName',
            relativePathParameterName: 'relativePath',
            currentChunkSizeParameterName: 'currentChunkSize',
            typeParameterName: 'type',
            fileType: this.restrictedToContentTypes && this.restrictedToContentTypes.length > 0 ? _.uniq(this.restrictedToContentTypes.map(x => x.extension)) : [],
            fileTypeErrorCallback: () => {

                this.viewModel.errors.push('InvalidFileType');

                this.viewModel.loading = false;
            },
            minFileSizeErrorCallback: () => {

                this.viewModel.errors.push('FileToSmall');

                this.viewModel.loading = false;
            }
        });

        if (resumable.support) {

            ConsoleFacade.log('File upload supported.');

        } else {

            ConsoleFacade.log('File upload not supported.');

            return;
        }

        resumable.assignBrowse(this.$refs.fileBrowseButton as Element, false);
        resumable.assignDrop(this.$refs.fileDropTarget as Element, false);

        resumable.on('fileAdded', (resumableFile: any) => {

            ConsoleFacade.log('File added.', resumableFile);

            this.viewModel.loading = true;

            if (this.maxFileSizeInBytes !== undefined && resumableFile.file.size >= this.maxFileSizeInBytes) {

                ConsoleFacade.log('File to large.', resumableFile, this.maxFileSizeInBytes);

                this.viewModel.errors.push('FileToLarge');

                resumable.cancel();

                this.viewModel.loading = false;

                return;

            }

            const abortByInvalidFileType = (error: FileUploadErrors) => {

                this.viewModel.errors.push(error);

                resumable.cancel();

                this.viewModel.loading = false;
            };

            if (!resumableFile.file) {

                ConsoleFacade.log('Missing file', resumableFile, this.restrictedToContentTypes);

                abortByInvalidFileType('InvalidFileType');

                return;
            }

            if (this.restrictedToContentTypes.length > 0 && this.restrictedToContentTypes.findIndex(x => x.contentType === resumableFile.file.type) === undefined) {

                ConsoleFacade.log('Not allowed file format.', resumableFile, this.restrictedToContentTypes);

                abortByInvalidFileType('InvalidFileType');

                return;
            }

            resumable.upload();
        });

        resumable.on('fileSuccess', (file: any) => {

            this.fileName = file.fileName;

            uploadedFile = file.file;

            ConsoleFacade.log('File success.', file);
        });

        resumable.on('fileError', (file: any, plainMessage: string) => {

            const httpError: IHttpValidationError = JSON.parse(plainMessage);

            ConsoleFacade.log('File error.', file, httpError);

            if (httpError.message === 'InvalidRequest' && httpError.type === 'InvalidModelException') {

                if (Object.prototype.hasOwnProperty.call(httpError, 'type')) {

                    this.viewModel.errors.push('InvalidFileType');
                }

                resumable.cancel();

                this.emitHttpError(httpError);

                this.viewModel.loading = false;
            }
        });

        resumable.on('complete', async () => {

            if (this.fileName === '') {
                return;
            }

            ConsoleFacade.log('Upload complete.');

            let fileBucketFile: IFileBucketFile;

            try {

                fileBucketFile = await this.apiClient.fileBucket.consolidate(this.fileName);

            } catch (e) {

                this.emitHttpError(e as IHttpError);

                return;
            }

            this.emitFileUploaded(fileBucketFile, uploadedFile);

            this.viewModel.loading = false;
        });

    }
}
